import React, { useState, useRef, useEffect } from "react";
import {
  Drawer,
  Row,
  Col,
  Typography,
  Button,
  Form,
  Space,
  Input,
  Select,
  message,
  DatePicker,
  Divider,
} from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import btnStyles from "../../../styles/Custom/Button.module.css";
import { validateConfig } from "../../../config/validate.config";
import inputStyles from "../../../styles/Custom/Input.module.css";
import { genderKeys } from "../receptionKeys";
import {
  getPartnerOrgsApi,
  partnerOnboardApi,
  updatepartnerOnboardApi,
} from "../../../services/partner.services";
import dayjs from "dayjs";
import useIsMobile from "../../../hooks/useIsMobile";
import useReceptionistMobilePickerProps from "../receptionistMobilePickerProps";
import "../receptionistMobileForms.css";

const { Option } = Select;

const dateFormat = "YYYY-MM-DD";
const OnboardingForm = ({ open, setOpen, editData, refresh }) => {
  const isMobile = useIsMobile();
  const mobilePickerProps = useReceptionistMobilePickerProps();
  const [form] = Form.useForm();
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [partnerOrgs, setPartnerOrgs] = useState([]);
  const [newPartnerOrg, setNewPartnerOrg] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editData?.id) {
      form.setFieldsValue({
        ...editData,
        dob: editData?.dob && dayjs(editData?.dob),
      });
    }
    getPartnerOrgsApi()
      .then((res) => setPartnerOrgs(res?.data?.data))
      .catch((err) => {
        console.log(err);
      });
  }, []);

  const onFinish = (val) => {
    const formData = { ...val, dob: dayjs(val?.dob).format(dateFormat) };
    if (editData?.id) {
      updatepartnerOnboardApi(editData?.user_id, formData)
        .then((res) => {
          refresh();
          message.success(res?.data?.message);
          setOpen(false);
        })
        .catch((err) => {
          message?.error(err?.response?.data?.message);
          console.log(err);
        });
    } else {
      partnerOnboardApi(formData)
        .then((res) => {
          refresh();
          message.success(res?.data?.message);
          setOpen(false);
        })
        .catch((err) => {
          message?.error(err?.response?.data?.message);
          console.log(err);
        });
    }
  };

  function disabledDate(current) {
    return current && current > dayjs().endOf("day");
  }

  const handleAddNewPartnerOrg = () => {
    if (newPartnerOrg) {
      const isExisting = partnerOrgs.some(
        (option) => option.toLowerCase() === newPartnerOrg.toLowerCase()
      );
      if (!isExisting) {
        setPartnerOrgs((prev) => [...prev, newPartnerOrg]);
      }
      setNewPartnerOrg("");
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };
  return (
    <div>
      <Drawer
        width={isMobile ? "100%" : "40%"}
        rootClassName={isMobile ? "reception-drawer-fullscreen" : undefined}
        open={open}
        title={
          <Row>
            <Col span={20} className="flex-start">
              <Typography
                style={{
                  marginLeft: "12px",
                  fontWeight: "400",
                  fontSize: "20px",
                }}
              >
                {editData?.id ? "Edit Partner" : "Add Partner"}
              </Typography>
            </Col>

            <Col span={4} className="flex-end">
              <CloseOutlined onClick={() => setOpen(false)} />
            </Col>
          </Row>
        }
        onClose={() => setOpen(false)}
        closeIcon={null}
        footer={
          <div className="flex-end" style={{ gap: "24px" }}>
            <Button
              loading={loading}
              htmlType="submit"
              type="primary"
              className={btnStyles.mdBtn}
              onClick={() => {
                if (formRef.current) {
                  formRef.current.submit();
                }
              }}
            >
              Submit
            </Button>
          </div>
        }
      >
        <Form layout="vertical" onFinish={onFinish} form={form} ref={formRef}>
          <Form.Item
            label="Contact Number"
            name="mobile"
            rules={[
              { required: true, message: "Please enter mobile number" },
              {
                pattern: /^[0-9]{10}$/,
                message: "Please enter a valid 10-digit phone number",
              },
            ]}
          >
            <Input
              style={{ width: "300px" }}
              maxLength="10"
              placeholder="Mobile number"
              addonBefore={"+91"}
              className={inputStyles.mdAddonInput}
            />
          </Form.Item>
          <Space>
            <Form.Item
              label="First Name"
              name="first_name"
              rules={validateConfig.isRequired("First Name")}
            >
              <Input
                className={inputStyles.mdInput}
                style={{ width: "250px" }}
                placeholder="Enter first name"
              />
            </Form.Item>
            <Form.Item
              label="Last Name"
              name="last_name"
              rules={validateConfig.isRequired("Last Name")}
            >
              <Input
                className={inputStyles.mdInput}
                style={{ width: "250px" }}
                placeholder="Enter last name"
              />
            </Form.Item>
          </Space>

          <Space>
            <Form.Item label="Date of Birth" name="dob">
              <DatePicker
                {...mobilePickerProps}
                disabledDate={disabledDate}
                className={inputStyles.mdDateInput}
                format={dateFormat}
                placeholder="Select date of birth"
                style={{ width: isMobile ? "100%" : "200px" }}
              />
            </Form.Item>
            <Form.Item
              label="Gender"
              name="gender"
              rules={[{ required: true, message: "Please select gender" }]}
            >
              <Select
                allowClear
                placeholder="Select Gender"
                className={inputStyles.mdSelect}
                style={{ width: "180px" }}
                options={genderKeys}
              />
            </Form.Item>
          </Space>

          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: "Please enter Email Id" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input
              placeholder="Enter email"
              className={inputStyles.mdInput}
              style={{ width: "300px" }}
            />
          </Form.Item>

          <Form.Item label="Address" name="address">
            <Input.TextArea
              placeholder="Enter address"
              className={inputStyles.mdInput}
              style={{ width: "300px" }}
            />
          </Form.Item>
          <Form.Item
            label="Choose Hospital"
            name="partner_organization"
            rules={[
              {
                required: true,
                message: "Please choose hospital / organisation",
              },
            ]}
          >
            <Select
              showSearch
              placeholder="Choose Hospital"
              size="large"
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: "8px 0" }} />
                  <Space style={{ padding: "0 8px 4px" }}>
                    <Input
                      placeholder="Add new Organization"
                      ref={inputRef}
                      value={newPartnerOrg}
                      onChange={(e) => setNewPartnerOrg(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                    />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={handleAddNewPartnerOrg}
                    >
                      Add
                    </Button>
                  </Space>
                </>
              )}
            >
              {partnerOrgs?.map((item, index) => (
                <Option value={item} key={index}>
                  {item}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label="Specilization"
            name={["experience", "specilization"]}
          >
            <Input
              placeholder="Enter Specilization"
              className={inputStyles.mdInput}
              style={{ width: "300px" }}
            />
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea
              placeholder="Enter notes"
              className={inputStyles.mdInput}
              style={{ width: "300px" }}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default OnboardingForm;
